import { useEffect, useState } from "react"

import EventEmitter from "src/EventEmitter"

export type VocabularyTranslation = {
  code: string
  words: {
    word: string
  }[]
}

export type VocabularyWord = {
  id: string
  word: string
  spokenForms: string[]
  partOfSpeech?: string | null
  definition: string
  translations: VocabularyTranslation[]
  occurrenceCount: number
  lastSpokenAt: string
}

type VocabularyWordEvent = VocabularyWord & {
  classPeriodId: string
}

type UseVocabularyWordsOptions = {
  classPeriodId?: string
  initialWords?: VocabularyWord[]
}

const byLastSpokenAt = (word: VocabularyWord, otherWord: VocabularyWord) =>
  new Date(word.lastSpokenAt).getTime() -
  new Date(otherWord.lastSpokenAt).getTime()

const useVocabularyWords = ({
  classPeriodId,
  initialWords = [],
}: UseVocabularyWordsOptions) => {
  const [liveWords, setLiveWords] = useState<VocabularyWord[]>([])
  const [liveWordsPeriodId, setLiveWordsPeriodId] = useState(classPeriodId)

  if (liveWordsPeriodId !== classPeriodId) {
    setLiveWordsPeriodId(classPeriodId)
    setLiveWords([])
  }

  useEffect(() => {
    const onVocabularyWord = ({
      classPeriodId: eventClassPeriodId,
      ...word
    }: VocabularyWordEvent) => {
      if (eventClassPeriodId !== classPeriodId) return

      setLiveWords(currentWords => [
        ...currentWords.filter(currentWord => currentWord.id !== word.id),
        word,
      ])
    }

    EventEmitter.subscribe("vocabulary_word", onVocabularyWord)
    return () => EventEmitter.unsubscribe("vocabulary_word", onVocabularyWord)
  }, [classPeriodId])

  const wordsById = new Map(initialWords.map(word => [word.id, word]))
  liveWords.forEach(word => wordsById.set(word.id, word))
  const words = Array.from(wordsById.values()).sort(byLastSpokenAt)
  const latestWordId = words[words.length - 1]?.id
  return { words, latestWordId }
}

export default useVocabularyWords
